import { cn } from '@mint-lib/tw-utils';

import { Label } from '../../lib/label.jsx';
import { ProgressIndicator, ProgressRoot } from '../../lib/progress.jsx';

type ProgressLinearProps = {
  /**
   * If `true`, the progress will have a label.
   * @default false
   */
  withLabel?: boolean;
  /**
   * The color of the progress.
   * @default 'primary'
   */
  color?: 'primary' | 'warning';
  /**
   * If `false`, the progress will be indeterminate, otherwise it will be determinate.
   * @default true
   */
  determinate?: boolean;
} & React.ComponentProps<typeof ProgressRoot>;

function ProgressLinear({
  withLabel = false,
  color = 'primary',
  determinate = true,
  className,
  ...props
}: ProgressLinearProps) {
  const percentage = Number(props.value ?? 0);
  const rootProps = determinate ? props : { ...props, value: undefined };

  return (
    <div className="flex h-1 w-full items-center justify-center gap-2">
      <ProgressRoot
        data-slot="progress"
        data-color={color}
        data-determinate={determinate}
        className={cn(
          'relative flex h-1 w-full items-center gap-2 overflow-hidden rounded-[2px]',
          className,
        )}
        {...rootProps}
      >
        <ProgressIndicator asChild>
          {determinate ? (
            <svg className="block h-full w-full">
              <rect
                data-slot="progress-linear-track"
                width="100%"
                height="4"
                rx="2"
                className="rounded-[2px] fill-greyscale-grey30"
              />
              <rect
                data-slot="progress-linear-indicator"
                data-color={color}
                width={`${percentage}%`}
                height="4"
                rx="2"
                className={cn(
                  'rounded-[2px] transition-all duration-300 ease-in-out',
                  'data-[color=primary]:fill-tertiary-light',
                  'data-[color=warning]:fill-warning-main',
                )}
              />
            </svg>
          ) : (
            <svg className="block h-full w-full">
              <rect
                data-slot="progress-linear-track"
                width="100%"
                height="4"
                rx="2"
                className="rounded-[2px] fill-greyscale-grey30"
              />
              <g
                data-slot="progress-linear-indicator"
                className="animate-progress-linear-indeterminate"
              >
                <rect
                  data-color={color}
                  width="60%"
                  height="4"
                  rx="2"
                  className={cn(
                    'rounded-[2px]',
                    'data-[color=primary]:fill-tertiary-light',
                    'data-[color=warning]:fill-warning-main',
                  )}
                />
              </g>
            </svg>
          )}
        </ProgressIndicator>
      </ProgressRoot>
      {withLabel && determinate && (
        <Label variant="caption1" color="secondary">
          {props.value}%
        </Label>
      )}
    </div>
  );
}

export default ProgressLinear;
